import { Injectable, signal, type Provider } from '@angular/core';

import { ok } from '../../../core/models/api-result';
import { HomeTopService } from './home-top.service';
import type { HomeTopPage, TopStoryEntry, TopStorySlot } from './home-top.models';
import { HUBS_PREVIEW_ITEMS } from './hubs-preview.fixtures';

/**
 * HOME'S TOP, WITHOUT A SESSION (`/preview/hubs`).
 *
 * Shaped after Dean's 01f and C1: your division's story leads (an article),
 * the parent organisation's town hall replay is second (a video). Two
 * must-reads due in five days sit in Today's brief, not the top: one is a front
 * page slot, one the overflow, and both are more than two days out.
 *
 * `?top=N` on the harness URL draws N stories (0 to 5; 4 by default, as in
 * 02b) so every layout can be looked at: 1 is the lead alone, 3 and up bring
 * the must-read due tomorrow into the row beneath (and out of the brief).
 * 0 leaves the front page only must-reads the top does not show, as a reader
 * who has acknowledged several gets: the top then falls back to the stream.
 *
 * Every slot points at a real row in `HUBS_PREVIEW_ITEMS`, so Read and Watch
 * open something.
 *
 * `?home=v1` on the harness URL turns the flag off, so the old Everything
 * can still be looked at (and journeyed) beside the new one.
 */

const DAY = 86_400_000;

function at(offsetMs: number): string {
  return new Date(Date.now() + offsetMs).toISOString();
}

function slot(partial: Partial<TopStorySlot> & Pick<TopStorySlot, 'id' | 'itemId'>): TopStorySlot {
  return {
    order: 1,
    priority: 'FEATURED',
    panelArt: null,
    panelLabel: null,
    strapline: null,
    liveEventId: null,
    liveEvent: null,
    external: null,
    target: 'CONTENT',
    item: null,
    ackPolicy: null,
    viewerAcknowledgedAt: null,
    scope: null,
    window: { startsAt: at(-DAY), endsAt: at(7 * DAY) },
    ...partial,
  };
}

/**
 * Five days on at 17:00: a must-read comfortably outside its last two days,
 * whatever day the harness is opened, so it stays in the brief.
 */
function inFiveDaysAtFive(): string {
  const d = new Date(Date.now() + 5 * DAY);
  d.setHours(17, 0, 0, 0);
  return d.toISOString();
}

/** Tomorrow at 17:00: inside a must-read's last two days. */
function tomorrowAtFive(): string {
  const d = new Date(Date.now() + DAY);
  d.setHours(17, 0, 0, 0);
  return d.toISOString();
}

const RDX = { unitId: 'rdx', unitName: 'RDx', unitType: 'DIVISION', depth: 1 } as const;
const EUROPE = { unitId: 'eu', unitName: 'Ricoh Europe', unitType: 'DIVISION', depth: 0 } as const;
const OFFICE = { unitId: 'rum-runner', unitName: 'Rum Runner Works', unitType: 'SITE', depth: 2 } as const;

/** The stories, in the order the composition ranks them. `?top=N` takes the first N. */
function stories(): TopStoryEntry[] {
  return [
    {
      kind: 'SLOT',
      reason: 'DIVISION_LEAD',
      reasonText: 'Pinned by your division, RDx.',
      slot: slot({
        id: 'top-rdx',
        itemId: 'rdx-1',
        order: 1,
        panelArt: '/assets/hubs-preview/pro-team.jpg',
        panelLabel: 'Workplace V3 is live with our first five customers',
        strapline: 'What launched, who is using it, and what we learned in week one.',
        item: {
          id: 'rdx-1',
          title: 'Why we rebuilt the desk picker twice',
          kind: 'article',
          hasMedia: true,
          mediaUrl: '/assets/hubs-preview/pro-team.jpg',
          posterUrl: null,
          playbackUrl: null,
        },
        scope: RDX,
        window: { startsAt: at(-DAY), endsAt: at(11 * DAY) },
      }),
    },
    {
      kind: 'SLOT',
      reason: 'ORGANISATION',
      reasonText: 'Pinned by Ricoh Europe.',
      slot: slot({
        id: 'top-eu',
        itemId: 'eu-1',
        order: 2,
        panelArt: '/assets/hubs-preview/pro-keynote.jpg',
        panelLabel: 'Half-year town hall: the replay',
        strapline: 'Jump to the Workplace V3 update at 18:40, or the open Q&A at 31:05.',
        item: {
          id: 'eu-1',
          title: 'The chief executive on the quarter, unedited',
          kind: 'video',
          hasMedia: true,
          mediaUrl: null,
          posterUrl: '/assets/hubs-preview/pro-keynote.jpg',
          playbackUrl: null,
        },
        scope: EUROPE,
        window: { startsAt: at(-3 * DAY), endsAt: at(5 * DAY) },
      }),
    },
    {
      kind: 'SLOT',
      reason: 'MUST_READ',
      reasonText: 'Compulsory reading from Ricoh Europe.',
      slot: slot({
        id: 'top-eu-benefits',
        itemId: 'eu-0',
        order: 3,
        priority: 'MANDATORY',
        panelArt: '/assets/hubs-preview/pro-atrium.jpg',
        panelLabel: 'Benefits enrolment closes tomorrow',
        strapline: 'Choose or confirm your cover for 2027.',
        item: {
          id: 'eu-0',
          title: 'Q3 FY25: what the results mean for us',
          kind: 'article',
          hasMedia: true,
          mediaUrl: '/assets/hubs-preview/pro-atrium.jpg',
          posterUrl: null,
          playbackUrl: null,
        },
        ackPolicy: { requiredBy: tomorrowAtFive(), remindEveryHours: 24 },
        scope: EUROPE,
      }),
    },
    {
      kind: 'SLOT',
      reason: 'SITE',
      reasonText: 'From your office, Rum Runner Works.',
      slot: slot({
        id: 'top-office',
        itemId: 'bhm-1',
        order: 4,
        panelLabel: 'Where everything moved on Level 3',
        item: {
          id: 'bhm-1',
          title: 'Where everything moved on Level 3',
          kind: 'article',
          hasMedia: true,
          mediaUrl: '/assets/hubs-preview/pro-reception.jpg',
          posterUrl: null,
          playbackUrl: null,
        },
        scope: OFFICE,
      }),
    },
    {
      kind: 'SLOT',
      reason: 'MOST_LOVED',
      reasonText: 'Loved across your hubs this week.',
      slot: slot({
        id: 'top-loved',
        itemId: 'rdx-4',
        order: 5,
        panelLabel: 'The release notes nobody reads, summarised',
        item: {
          id: 'rdx-4',
          title: 'The release notes nobody reads, summarised',
          kind: 'article',
          hasMedia: false,
          mediaUrl: null,
          posterUrl: null,
          playbackUrl: null,
        },
        scope: RDX,
      }),
    },
  ];
}

/** The must-reads due in five days: more than two days out, so the brief's. */
function briefOnly(): TopStoryEntry[] {
  return [
    {
      kind: 'SLOT',
      reason: 'MUST_READ',
      reasonText: 'Compulsory reading from RDx.',
      slot: slot({
        id: 'top-rdx-security',
        itemId: 'rdx-2',
        order: 6,
        priority: 'MANDATORY',
        panelLabel: 'Security refresher: phishing in 2026',
        item: {
          id: 'rdx-2',
          title: 'Watch a floor plan become a map',
          kind: 'article',
          hasMedia: false,
          mediaUrl: null,
          posterUrl: null,
          playbackUrl: null,
        },
        ackPolicy: { requiredBy: inFiveDaysAtFive(), remindEveryHours: 24 },
        scope: RDX,
      }),
    },
    {
      kind: 'MANDATORY_OVERFLOW',
      reason: 'MUST_READ',
      reasonText: 'Compulsory reading from Ricoh Europe.',
      slot: null,
      overflowSlots: [
        slot({
          id: 'top-eu-policy',
          itemId: 'eu-4',
          order: 7,
          priority: 'MANDATORY',
          panelLabel: 'New travel and expenses policy',
          item: {
            id: 'eu-4',
            title: 'Forty thousand pages, one policy',
            kind: 'article',
            hasMedia: false,
            mediaUrl: null,
            posterUrl: null,
            playbackUrl: null,
          },
          ackPolicy: { requiredBy: inFiveDaysAtFive(), remindEveryHours: 24 },
          scope: EUROPE,
        }),
      ],
    },
  ];
}

/**
 * The front page. `count` is how many top stories it carries (default 4);
 * the brief's two must-reads are always there behind them.
 */
export function previewHomeTopPage(count = 4): HomeTopPage {
  const n = Math.max(0, Math.min(5, Math.round(count)));
  return {
    collapsedToTicker: false,
    entries: [...stories().slice(0, n), ...briefOnly()],
  };
}

/** The harness's story count: `#/preview/hubs?top=2`. 4 when absent. */
export function previewTopCount(): number {
  if (typeof location === 'undefined') return 4;
  const match = /[?&]top=(\d)\b/.exec(location.hash + location.search);
  return match ? Number(match[1]) : 4;
}

/** The harness's off switch: `#/preview/hubs?home=v1`. */
export function previewHomeV1(): boolean {
  return typeof location !== 'undefined' && /[?&]home=v1\b/.test(location.hash + location.search);
}

@Injectable()
export class HomeTopPreviewService extends HomeTopService {
  /**
   * Live, not read once: the route's injector outlives a hash change, so a
   * journey that moves from the harness to `?home=v1` without a reload must
   * still see the switch.
   */
  private readonly v1 = signal(previewHomeV1());

  constructor() {
    super();
    if (typeof window !== 'undefined') {
      window.addEventListener('hashchange', () => this.v1.set(previewHomeV1()));
    }
  }

  protected override forcedOff(): boolean {
    return this.v1();
  }

  protected override async fetch() {
    return ok(previewHomeTopPage(previewTopCount()));
  }

  protected override async resolveItems(ids: readonly string[]): Promise<void> {
    this.setItems(HUBS_PREVIEW_ITEMS.filter(item => ids.includes(item.id)));
  }
}

export const HOME_TOP_PREVIEW_PROVIDER: Provider = {
  provide: HomeTopService,
  useClass: HomeTopPreviewService,
};
